import React from 'react';

import SimpleCheckbox from '../../../../components/Checkbox/SimpleCheckbox';
import MediaObject from '../../../../components/MediaObject';
import ProgressBar from '../../../../components/HorizontalBars/ProgressBar';

import { initParams } from '../../configs';
import { isUnified, demoPagePath } from '../../../../utils/helpers';
import { format } from '../../../../utils/date';
import formatter, { numberToHumanTable } from '../../../../utils/formatter';

const { store, permissions } = initParams;

export const mainColumns = [
  { dataField: 'app_name', sortBy: 'name', text: 'App', sortable: true, style: { width: '30%' } },
  {
    dataField: 'price',
    sortBy: 'price',
    text: 'Price',
    sortable: true,
    className: 'text-right',
  },
  {
    dataField: 'released_on',
    sortBy: 'released_on',
    text: 'Released',
    sortable: true,
    className: 'text-right',
  },
  {
    dataField: 'downloads',
    sortBy: 'downloads',
    sortable: true,
    className: 'text-nowrap text-xxs-right text-blue',
    element: (
      <span>
        <i className="hicon-download mr-xxxs" />
        Downloads
      </span>
    ),
  },
  {
    dataField: 'total_revenue',
    sortBy: 'total_revenue',
    sortable: true,
    className: 'text-nowrap text-right text-green',
    element: (
      <span>
        <i className="hicon-dollar mr-xxxs" />
        Revenue
      </span>
    ),
  },
  {
    dataField: 'dau',
    sortBy: 'dau',
    sortable: true,
    className: 'text-nowrap text-right',
    element: (
      <span>
        <i className="hicon-dau mr-xxxs" />
        Avg DAU
      </span>
    ),
  },
  {
    dataField: 'mau',
    sortBy: 'mau',
    sortable: true,
    className: 'text-nowrap text-right',
    element: (
      <span>
        <i className="hicon-mau mr-xxxs" />
        Avg MAU
      </span>
    ),
  },
];

export const checkboxColumn = {
  dataField: 'checkbox',
  text: '',
  className: 'text-center',
  style: { width: 40, paddingLeft: 24, verticalAlign: 'middle' },
};

export const columnsDataFields = (
  permissions.allowMyMarkets && !permissions.isFlexUser ? ['checkbox'] : []
).concat(mainColumns.map((c) => c.dataField));

const renderMetricCell = ({ value, decorate, totals, totalKey, formattedValue }) => {
  if (isUnified(store) && totals && totals[totalKey] !== null) {
    const pct = totals[totalKey] > 0 ? (value / totals[totalKey]) * 100 : 0;

    return decorate(
      <div className="d-flex flex-items-xxs-middle justify-content-end text-right">
        <span className="m-r-xs">{formattedValue}</span>
        <ProgressBar
          percentage={pct}
          showBarOnly
          backgroundColor={totalKey === 'total_revenue' ? '#3CBD67' : '#848ea1'}
        />
      </div>
    );
  }
  return decorate(formattedValue);
};

const formatReleasedOn = (releasedOn) => {
  if (releasedOn && releasedOn !== 'N/A') {
    return format(releasedOn, 'MMM DD, YYYY');
  }

  return releasedOn || '—';
};

export const buildFreeDataRows = ({ apps, isProspect, isFlexUser }) =>
  apps.map((app, index) => {
    const iconUrl = app.cdn_images ? app.cdn_images['32x32'] : app.icon_url;
    const iconUrl2x = app.cdn_images ? app.cdn_images['64x64'] : app.icon_url;
    return {
      key: app.ref_no,
      app_name: (
        <MediaObject
          {...app}
          kind="app"
          appIconBordered
          app_id={app.ref_no}
          store={app.store}
          icon_url={iconUrl}
          icon_url_2x={iconUrl2x}
          appIconSize={32}
          withoutStore={!isUnified(store)}
          storeIconClass="text-xxs text-quiet"
          className="app-cell"
          appQuickViewAllowed={false}
        />
      ),
      price: <td className="text-right">{app.price !== 0 ? `$${app.price}` : 'Free'}</td>,
      released_on: <td className="text-right  b-r">{formatReleasedOn(app.released_on)}</td>,
      downloads:
        index === 0 ? (
          <td className="bg-gray" colSpan={4} rowSpan={apps.length || 10}>
            {isProspect && (
              <>
                <p className="publisher-performance-text">
                  Publisher performance data is available on any paid plan.
                </p>
                <p className="text-xxs-center">
                  <a
                    href={demoPagePath()}
                    className="publishers-demo-btn hms-btn hms-btn--primary hms-btn--lg"
                  >
                    Request a Demo
                  </a>
                </p>
              </>
            )}
            {!isProspect && isFlexUser && (
              <p className="publisher-performance-text">Not included in your plan</p>
            )}
            {!isProspect && !isFlexUser && (
              <p className="text-xxs-center">
                <a href="/pricing">Upgrade</a>
                &nbsp;to see this data
              </p>
            )}
          </td>
        ) : (
          <></>
        ),
    };
  });

const METRIC_KEYS = ['mau', 'dau', 'downloads', 'total_revenue'];
const hasNoMetrics = (app) => METRIC_KEYS.every((key) => app[key] === null);

export const buildDataRows = ({
  apps,
  totals,
  selectedApps,
  limitReached,
  isFlexUser,
  onAppQuickViewOpen,
  onPubQuickViewOpen,
  onAppCheckboxChange,
}) => {
  const noDataStartIndex = isFlexUser ? apps.findIndex(hasNoMetrics) : -1;

  return apps.map((app, index) => {
    const isAvailable = app.available !== false;
    const checked = isAvailable && selectedApps.some((selected) => selected.ref_no === app.ref_no);
    const decorate = (cell) =>
      isAvailable ? <td className="text-right">{cell}</td> : <span>—</span>;
    const iconUrl = app.cdn_images ? app.cdn_images['32x32'] : app.icon_url;
    const iconUrl2x = app.cdn_images ? app.cdn_images['64x64'] : app.icon_url;
    const isNoDataRow = noDataStartIndex !== -1 && index >= noDataStartIndex;

    const metricCells = isNoDataRow
      ? {
          released_on: <td className="text-right b-r">{formatReleasedOn(app.released_on)}</td>,
          downloads:
            index === noDataStartIndex ? (
              <td className="bg-gray" colSpan={4} rowSpan={apps.length - noDataStartIndex}>
                <p className="publisher-performance-text">Not included in your plan</p>
              </td>
            ) : (
              <></>
            ),
          total_revenue: <></>,
          dau: <></>,
          mau: <></>,
        }
      : {
          downloads: <td className="text-right">{numberToHumanTable(app.downloads)}</td>,
          total_revenue: renderMetricCell({
            value: app.total_revenue,
            decorate,
            totals,
            totalKey: 'total_revenue',
            formattedValue: `$${numberToHumanTable(
              app.total_revenue,
              formatter.downloadsAbbreviationSettings
            )}`,
          }),
          dau: renderMetricCell({
            value: app.dau,
            decorate,
            totals,
            totalKey: 'dau',
            formattedValue: numberToHumanTable(app.dau, formatter.downloadsAbbreviationSettings),
          }),
          mau: renderMetricCell({
            value: app.mau,
            decorate,
            totals,
            totalKey: 'mau',
            formattedValue: numberToHumanTable(app.mau, formatter.downloadsAbbreviationSettings),
          }),
        };

    return {
      key: app.ref_no,
      rowClassName: isAvailable ? '' : 'row-disabled',
      app_name: (
        <MediaObject
          {...app}
          kind="app"
          appIconBordered
          app_id={app.ref_no}
          store={app.store}
          icon_url={iconUrl}
          icon_url_2x={iconUrl2x}
          appIconSize={32}
          withoutStore={!isUnified(store)}
          storeIconClass="text-xxs text-quiet"
          className="app-cell"
          disableLink={!isAvailable}
          appQuickViewAllowed={permissions.allowAppQuickView}
          onAppQuickViewOpen={onAppQuickViewOpen}
          onPubQuickViewOpen={onPubQuickViewOpen}
        />
      ),
      price: <td className="text-right">{app.price !== 0 ? `$${app.price}` : 'Free'}</td>,
      released_on: <td className="text-right">{formatReleasedOn(app.released_on)}</td>,
      ...metricCells,
      ...(permissions.allowMyMarkets && {
        checkbox: (
          <SimpleCheckbox
            id={app.ref_no?.toString()}
            checked={checked}
            disabled={!isAvailable || (!checked && limitReached())}
            onChange={(next) => onAppCheckboxChange(next, app)}
          />
        ),
      }),
    };
  });
};
